import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import classNames from "classnames"
import { toInteger } from "lodash-es"

import { useTranslation } from "@/locales"
import { getFontSize, loadImage } from "@/utils/misc"

import ImgBad from "../../assets/img/record/pie/bad.png"
import ImgBg from "../../assets/img/record/pie/bg.png"
import ImgGood from "../../assets/img/record/pie/good.png"
import ImgNormal from "../../assets/img/record/pie/normal.png"
import styles from "./index.module.less"

interface Vector2 {
    x: number
    y: number
}

interface LabelProps {
    percent: number
    value: number
    type: string
    style?: React.CSSProperties
}

const Label: React.FC<LabelProps> = (props) => {
    const { type, percent, value, style } = props
    const { t } = useTranslation()

    return (
        <div className={styles.label} style={style}>
            <div className={styles.percent}>{percent}%</div>
            <div className={styles.row}>
                <span className={styles.bold}>
                    {t("player.copper.draw.prefix")}
                </span>
                <span
                    className={classNames(styles.highlight, styles.bold)}
                    data-type={type}
                >
                    {t(`player.copper.draw.${type}`)}
                </span>
                <span className={styles.cross}>x</span>
                <span className={styles.value}>{value}</span>
            </div>
        </div>
    )
}

const getRotateAngle = (data: { value: number }[], total: number) => {
    let angle = 0
    if (data.length < 3) {
        return angle
    }

    for (let index = 0; index < data.length; index++) {
        const current = data[index]
        const next = data[(index + 1) % data.length]
        angle -= (current.value / total) * 2 * Math.PI
        if ((current.value + next.value) / total <= 0.5) {
            return angle
        }
    }

    return 0
}

interface FragmentPieChartProps {
    data: { type: string; value: number }[]
}

const strokeColor = {
    GOOD: "#ffaac8",
    NORMAL: "#00ffc4",
    BAD: "#fa7171",
}

const getOffCanvas = (canvas: HTMLCanvasElement) => {
    const offCanvas = document.createElement("canvas")
    offCanvas.width = canvas.width
    offCanvas.height = canvas.height
    return {
        canvas: offCanvas,
        ctx: offCanvas.getContext("2d"),
    }
}

const CopperDraw: React.FC<FragmentPieChartProps> = ({ data }) => {
    const lastSize = useRef(0)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const lineCanvasRef = useRef<HTMLCanvasElement>(null)

    const [endPositions, setEndPositions] = useState<Vector2[]>([
        { x: 0, y: 0 },
        { x: 0, y: 0 },
        { x: 0, y: 0 },
    ])

    const total = useMemo(
        () => data.reduce((sum, slice) => sum + slice.value, 0),
        [data]
    )
    const rotateAngle = useMemo(
        () => getRotateAngle(data, total),
        [data, total]
    )

    const drawPie = useCallback(async () => {
        const canvas = canvasRef.current
        const ctx = canvas?.getContext("2d")
        if (!canvas || !ctx) {
            return
        }

        const rem = 0.95
        const scale = canvas.clientHeight / (rem * getFontSize())
        const fontSize = getFontSize() * window.devicePixelRatio * scale
        const size = rem * fontSize
        canvas.width = size
        canvas.height = size

        const mask = getOffCanvas(canvas)
        const image = getOffCanvas(canvas)
        const shadow = getOffCanvas(canvas)
        if (!mask.ctx || !image.ctx || !shadow.ctx) {
            return
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height)

        const [background, good, normal, bad] = await Promise.all(
            [ImgBg, ImgGood, ImgNormal, ImgBad].map((image) => loadImage(image))
        )
        if (background) {
            ctx.drawImage(background, 0, 0, canvas.width, canvas.height)
        }
        const typeImages = {
            GOOD: good,
            NORMAL: normal,
            BAD: bad,
        }

        let startAngle = Math.PI / 2 + rotateAngle
        const x = canvas.width / 2
        const y = canvas.height / 2
        const radius = 1 * fontSize
        const padding = (15 / 160) * fontSize
        const padding2 = (40 / 160) * fontSize
        const innerSize = (122 / 160) * fontSize

        const drawRect = (ctx: CanvasRenderingContext2D) => {
            ctx.beginPath()
            ctx.moveTo(padding2, padding)
            ctx.lineTo(size - padding2, padding)
            ctx.lineTo(size - padding, padding2)
            ctx.lineTo(size - padding, size - padding2)
            ctx.lineTo(size - padding2, size - padding)
            ctx.lineTo(padding2, size - padding)
            ctx.lineTo(padding, size - padding2)
            ctx.lineTo(padding, padding2)
            ctx.closePath()
        }

        const drawPath = (ctx: CanvasRenderingContext2D, endAngle: number) => {
            ctx.beginPath()

            const isCircle = endAngle - startAngle === 2 * Math.PI
            if (isCircle) {
                ctx.arc(x, y, radius, 0, 2 * Math.PI)
            } else {
                ctx.moveTo(x, y)
                ctx.arc(x, y, radius, startAngle, endAngle)
                ctx.closePath()
            }
        }

        const drawImage = (
            inCtx: CanvasRenderingContext2D,
            inCanvas: HTMLCanvasElement
        ) => {
            inCtx.globalCompositeOperation = "destination-in"
            inCtx.drawImage(mask.canvas, 0, 0)
            inCtx.globalCompositeOperation = "source-over"
            ctx.drawImage(inCanvas, 0, 0)
        }

        for (const slice of data) {
            const sliceAngle = (slice.value / total) * 2 * Math.PI
            const endAngle = startAngle + sliceAngle
            ctx.save()

            mask.ctx.save()
            mask.ctx.fillStyle = "#fff"
            drawRect(mask.ctx)
            mask.ctx.fill()
            mask.ctx.globalCompositeOperation = "destination-in"
            drawPath(mask.ctx, endAngle)
            mask.ctx.fill()
            mask.ctx.restore()

            image.ctx.drawImage(
                typeImages[slice.type],
                padding,
                padding,
                innerSize,
                innerSize
            )
            drawImage(image.ctx, image.canvas)

            shadow.ctx.clearRect(
                0,
                0,
                shadow.canvas.width,
                shadow.canvas.height
            )
            shadow.ctx.strokeStyle = strokeColor[slice.type]
            shadow.ctx.lineWidth = 0.05 * fontSize
            shadow.ctx.filter = `blur(${0.02 * fontSize}px)`
            drawPath(shadow.ctx, endAngle)
            shadow.ctx.stroke()
            drawRect(shadow.ctx)
            shadow.ctx.stroke()
            drawImage(shadow.ctx, shadow.canvas)

            ctx.restore()
            startAngle = endAngle
        }
    }, [data, rotateAngle, total])

    const drawLine = useCallback(() => {
        const canvas = lineCanvasRef.current
        const ctx = canvas?.getContext("2d")
        if (!canvas || !ctx) {
            return
        }

        const fontSize = getFontSize() * window.devicePixelRatio
        const size = (0.94375 + 0.75) * fontSize * 10
        canvas.width = size
        canvas.height = size

        let startAngle = Math.PI / 2 + rotateAngle
        const centerX = canvas.width / 2
        const centerY = canvas.height / 2
        const radius = Math.min(centerX, centerY)

        ctx.clearRect(0, 0, canvas.width, canvas.height)

        const positions: Vector2[] = []

        data.forEach((slice) => {
            const sliceAngle = (slice.value / total) * 2 * Math.PI
            const midAngle = startAngle + sliceAngle / 2

            const startR = radius * 0.4
            const startX = centerX + startR * Math.cos(midAngle)
            const startY = centerY + startR * Math.sin(midAngle)

            const pointR = (2 / 16) * fontSize
            ctx.beginPath()
            ctx.arc(startX, startY, pointR, 0, pointR * Math.PI)
            ctx.fillStyle = "#fff"
            ctx.fill()

            const endR = radius * 0.7
            const endX = centerX + endR * Math.cos(midAngle)
            const endY = centerY + endR * Math.sin(midAngle)

            ctx.beginPath()
            ctx.moveTo(startX, startY)
            ctx.lineTo(endX, endY)

            const endAt = {
                x: 0,
                y: endY,
            }
            const fixedEndX = Number(endX.toFixed(1))
            if (fixedEndX < centerX) {
                endAt.x = Math.floor(canvas.width * 0.05)
            } else {
                endAt.x = Math.floor(canvas.width * 0.95)
            }
            ctx.lineTo(endAt.x, endAt.y)

            let _y = endAt.y - centerY - (20 / 16) * fontSize
            if (_y < 0) {
                _y -= fontSize
            }
            positions.push({
                x: fixedEndX < centerX ? -1 : 1,
                y: _y / window.devicePixelRatio,
            })

            ctx.strokeStyle = "#fff"
            ctx.lineWidth = (2 / 16) * fontSize
            ctx.stroke()

            startAngle = startAngle + sliceAngle
        })

        setEndPositions(positions)
    }, [data, rotateAngle, total])

    const draw = useCallback(() => {
        const fontSize = getFontSize()
        if (fontSize === lastSize.current) {
            return
        }
        lastSize.current = fontSize

        drawPie()
        drawLine()
    }, [drawLine, drawPie])

    useEffect(() => {
        draw()

        window.addEventListener("resize", draw)
        window.addEventListener("focus", draw)
        return () => {
            window.removeEventListener("resize", draw)
            window.removeEventListener("focus", draw)
        }
    }, [draw])

    const labelData = useMemo(() => {
        let rest = 100

        return data.map((item, index) => {
            const percent =
                index === data.length - 1
                    ? rest
                    : toInteger((item.value / total) * 100)
            rest -= percent

            return {
                type: item.type,
                value: item.value,
                percent,
            }
        })
    }, [data, total])

    return (
        <div className={styles.chart}>
            <div className={styles.pie}>
                <canvas ref={canvasRef} />
            </div>
            <canvas ref={lineCanvasRef} className={styles.line} />
            {labelData.map((item, index) => (
                <Label
                    key={index}
                    {...item}
                    style={
                        endPositions[index].x < 0
                            ? {
                                  left: 0,
                                  transform: `translate(-100%, ${endPositions[index].y}px)`,
                              }
                            : {
                                  right: 0,
                                  transform: `translate(100%, ${endPositions[index].y}px)`,
                              }
                    }
                />
            ))}
        </div>
    )
}

export default CopperDraw
